import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    LinearProgress,
    Snackbar
} from '@mui/material';
import {
    AddCircleOutline,
    BeachAccess,
    Pending,
    CheckCircle,
    Cancel,
    Schedule,
    Edit as EditIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useEmployeeLeaveStore, {
    LeaveBalance,
    LeaveRequest,
    LeaveType
} from '@/store/Employee/EmployeeLeaveStore';
import { useAuth } from '@/store/authStore';

const LeavePortal: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingLeave, setEditingLeave] = useState<LeaveRequest | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingLeave, setViewingLeave] = useState<LeaveRequest | null>(null);

    const {
        leaveBalance = [],
        pendingLeaves = [],
        approvedLeaves = [],
        declinedLeaves = [],
        loading,
        error,
        fetchLeaveTypes,
        fetchLeaveBalance,
        fetchPendingLeaves,
        fetchApprovedLeaves,
        fetchDeclinedLeaves,
        submitLeaveRequest,
        updateLeaveRequest,
        withdrawLeave,
    } = useEmployeeLeaveStore();

    // Debug: Log store state changes
    useEffect(() => {
        console.log('LeavePortal: Store state updated:', {
            leaveBalance: leaveBalance.length,
            pendingLeaves: pendingLeaves.length,
            approvedLeaves: approvedLeaves.length,
            declinedLeaves: declinedLeaves.length,
            loading,
            error
        });
    }, [ leaveBalance, pendingLeaves, approvedLeaves, declinedLeaves, loading, error]);

    const [leaveForm, setLeaveForm] = useState({
        leave_type_id: '',
        title: '',
        start_date: '',
        end_date: '',
        body: '',
    });

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [withdrawConfirm, setWithdrawConfirm] = useState<{ open: boolean; leaveId: number | null; title: string }>({
        open: false,
        leaveId: null,
        title: ''
    });
    const [withdrawing, setWithdrawing] = useState(false);

    // Start of current financial year (April 1) — earliest allowed leave date.
    // Allows backdating within the current FY (e.g. for offline/email-approved
    // leaves) while keeping leave-balance accounting correct.
    const getFinancialYearStartDate = () => {
        const today = new Date();
        const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
        return `${startYear}-04-01`;
    };

    useEffect(() => {
        if (user?.id) {
            console.log('LeavePortal: Fetching leave data for user:', user.id);

            fetchLeaveTypes()
                .then(data => console.log('LeavePortal: Leave types fetched:', data))
                .catch(err => console.error('LeavePortal: Error fetching leave types:', err));

            fetchLeaveBalance(user.id)
                .then(data => console.log('LeavePortal: Leave balance fetched:', data))
                .catch(err => console.error('LeavePortal: Error fetching leave balance:', err));

            fetchPendingLeaves(user.id)
                .then(data => console.log('LeavePortal: Pending leaves fetched:', data))
                .catch(err => console.error('LeavePortal: Error fetching pending leaves:', err));

            fetchApprovedLeaves(user.id)
                .then(data => console.log('LeavePortal: Approved leaves fetched:', data))
                .catch(err => console.error('LeavePortal: Error fetching approved leaves:', err));

            fetchDeclinedLeaves(user.id)
                .then(data => console.log('LeavePortal: Declined leaves fetched:', data))
                .catch(err => console.error('LeavePortal: Error fetching declined leaves:', err));
        } else {
            console.log('LeavePortal: No user ID found');
        }
    }, [user?.id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setLeaveForm((prev) => ({
            ...prev,
            [name as string]: value,
        }));
    };

    const handleSubmitLeave = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            await submitLeaveRequest(leaveForm);
            setSnackbar({
                open: true,
                message: 'Leave request submitted successfully!',
                severity: 'success'
            });
            setShowLeaveModal(false);
            setLeaveForm({
                leave_type_id: '',
                title: '',
                start_date: '',
                end_date: '',
                body: '',
            });
            if (user?.id) {
                fetchPendingLeaves(user.id);
                fetchLeaveBalance(user.id);
            }
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to submit leave request',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleWithdrawLeave = async () => {
        if (!withdrawConfirm.leaveId || !user?.id) return;
        setWithdrawing(true);
        try {
            await withdrawLeave(withdrawConfirm.leaveId, user.id);
            setSnackbar({
                open: true,
                message: 'Leave withdrawn successfully! Balance has been restored.',
                severity: 'success'
            });
            setWithdrawConfirm({ open: false, leaveId: null, title: '' });
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err.message || 'Failed to withdraw leave',
                severity: 'error'
            });
        } finally {
            setWithdrawing(false);
        }
    };

    const handleOpenEditModal = (leave: LeaveRequest) => {
        console.log('Opening edit modal with leave data:', {
            start_date_raw: leave.start_date,
            end_date_raw: leave.end_date,
            start_date_formatted: formatDateForInput(leave.start_date),
            end_date_formatted: formatDateForInput(leave.end_date)
        });

        setEditingLeave(leave);
        setLeaveForm({
            leave_type_id: leave.leave_type_id.toString(),
            title: leave.title,
            start_date: formatDateForInput(leave.start_date),
            end_date: formatDateForInput(leave.end_date),
            body: leave.body,
        });
        setShowEditModal(true);
    };

    const handleEditLeave = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!editingLeave || !user?.id) return;
        try {
            await updateLeaveRequest(editingLeave.leave_id, leaveForm, user.id);
            setSnackbar({
                open: true,
                message: 'Leave request updated successfully!',
                severity: 'success'
            });
            setShowEditModal(false);
            setEditingLeave(null);
            setLeaveForm({
                leave_type_id: '',
                title: '',
                start_date: '',
                end_date: '',
                body: '',
            });
            if (user?.id) {
                fetchPendingLeaves(user.id);
                fetchApprovedLeaves(user.id);
                fetchLeaveBalance(user.id);
            }
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err.message || 'Failed to update leave request',
                severity: 'error'
            });
        }
    };

    const handleViewLeave = (leave: LeaveRequest) => {
        setViewingLeave(leave);
        setShowViewModal(true);
    };

    const canWithdraw = (leave: LeaveRequest): boolean => {
        if (leave.status !== 'approved') return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(leave.start_date);
        startDate.setHours(0, 0, 0, 0);
        return startDate > today;
    };

    const canEdit = (leave: LeaveRequest): boolean => {
        // Only pending leaves can be edited
        if (leave.status === 'pending') return true;

        // Approved, declined, and withdrawn leaves cannot be edited
        return false;
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDateForInput = (dateString: string): string => {
        // IMPORTANT: Extract date string without any Date object conversion to avoid timezone issues

        if (!dateString) return '';

        // If string contains 'T' (ISO format), extract the date part before 'T'
        // Example: "2026-05-06T00:00:00.000Z" becomes "2026-05-06"
        const datePart = dateString.split('T')[0];

        // Validate it's in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
            return datePart;
        }

        // If not in expected format, try to parse and reconstruct
        // Use UTC methods to prevent timezone shifts
        try {
            const date = new Date(dateString + 'T00:00:00Z'); // Force UTC interpretation
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return dateString;
        }
    };

    const calculateDays = (fromDate: string, toDate: string) => {
        const start = new Date(fromDate);
        const end = new Date(toDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'success';
            case 'declined': return 'error';
            case 'pending': return 'warning';
            case 'withdrawn': return 'default';
            default: return 'default';
        }
    };

    const getLeaveTypeColor = (leaveType: string) => {
        const typeColors: { [key: string]: string } = {
            'Annual Leave': '#4caf50',
            'Sick Leave': '#ff9800',
            'Personal Leave': '#2196f3',
            'Maternity Leave': '#e91e63',
            'Paternity Leave': '#9c27b0',
            'Emergency Leave': '#f44336'
        };
        return typeColors[leaveType] || '#757575';
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const renderLeaveBalance = () => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)'
                },
                gap: 3
            }}
        >
            {leaveBalance.length > 0 ? (
                leaveBalance.map((balance: LeaveBalance) => (
                    <Card key={balance.id}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {balance.leave_name}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Leaves
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {balance.total_leaves}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Leaves Taken
                                    </Typography>
                                    <Typography variant="body2" color="error.main" fontWeight="bold">
                                        {balance.leaves_taken}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Remaining
                                    </Typography>
                                    <Typography variant="body2" color="success.main" fontWeight="bold">
                                        {balance.remaining_leaves}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={(balance.leaves_taken / balance.total_leaves) * 100}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: (balance.leaves_taken / balance.total_leaves) > 0.8 ? 'error.main' :
                                                (balance.leaves_taken / balance.total_leaves) > 0.6 ? 'warning.main' : 'success.main'
                                        }
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                    {Math.round((balance.leaves_taken / balance.total_leaves) * 100)}% Used
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            ) : (
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary', gridColumn: '1 / -1' }}>
                    <Typography>No leave balance available</Typography>
                </Box>
            )}
        </Box>
    );

    const renderLeaveRequests = (leaves: LeaveRequest[], emptyMessage: string, showActions: boolean = false, showEdit: boolean = false) => (
        <Card>
            <CardContent sx={{ p: 0 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Leave Type</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>From - To</TableCell>
                                <TableCell>Days</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Applied Date</TableCell>
                                {(showActions || showEdit) && <TableCell>Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaves.length > 0 ? (
                                leaves.map((leave: LeaveRequest) => (
                                    <TableRow key={leave.leave_id} hover>
                                        <TableCell>
                                            <Chip
                                                label={leave.leave_type || 'Unknown'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getLeaveTypeColor(leave.leave_type || ''),
                                                    color: 'white',
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {leave.title}
                                            </Typography>
                                            {leave.body && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {leave.body.substring(0, 50)}...
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {calculateDays(leave.start_date, leave.end_date)} days
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={leave.status.toUpperCase()}
                                                color={getStatusColor(leave.status) as any}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(leave.created_at)}
                                            </Typography>
                                        </TableCell>
                                        {(showActions || showEdit) && (
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="outlined"
                                                        color="info"
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => handleViewLeave(leave)}
                                                    >
                                                        View
                                                    </Button>
                                                    {showEdit && canEdit(leave) && (
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            startIcon={<EditIcon />}
                                                            onClick={() => handleOpenEditModal(leave)}
                                                        >
                                                            Edit
                                                        </Button>
                                                    )}
                                                    {showActions && canWithdraw(leave) && (
                                                        <Button
                                                            variant="outlined"
                                                            color="warning"
                                                            size="small"
                                                            onClick={() => setWithdrawConfirm({
                                                                open: true,
                                                                leaveId: leave.leave_id,
                                                                title: leave.title
                                                            })}
                                                        >
                                                            Withdraw
                                                        </Button>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={showActions || showEdit ? 7 : 6} align="center">
                                        <Box sx={{ p: 4, color: 'text.secondary' }}>
                                            <Typography>{emptyMessage}</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return renderLeaveBalance();
            case 1:
                return renderLeaveRequests(pendingLeaves, 'No pending leave requests', false, true);
            case 2:
                return renderLeaveRequests(approvedLeaves, 'No approved leave requests', true, false);
            case 3:
                return renderLeaveRequests(declinedLeaves, 'No declined leave requests', true, false);
            default:
                return null;
        }
    };

    if (loading && leaveBalance.length === 0) {
        return (
            <Layout>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth="xl">
                <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Leave Portal
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Manage your leave requests and track your leave balance
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddCircleOutline />}
                            onClick={() => setShowLeaveModal(true)}
                        >
                            Apply for Leave
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTab-root.Mui-selected': {
                                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'primary.main',
                                    fontWeight: 600
                                }
                            }}
                        >
                            <Tab label="Leave Balance" icon={<BeachAccess />} iconPosition="start" />
                            <Tab label={`Pending (${pendingLeaves.length})`} icon={<Pending />} iconPosition="start" />
                            <Tab label={`Approved (${approvedLeaves.length})`} icon={<CheckCircle />} iconPosition="start" />
                            <Tab label={`Declined (${declinedLeaves.length})`} icon={<Cancel />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    {/* Tab Content */}
                    {renderTabContent()}

                    {/* Leave Application Modal */}
                    <Dialog
                        open={showLeaveModal}
                        onClose={() => setShowLeaveModal(false)}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            sx: {
                                maxHeight: '85vh'
                            }
                        }}
                    >
                        <DialogTitle>Apply for Leave</DialogTitle>
                        <form onSubmit={handleSubmitLeave}>
                            <DialogContent
                                sx={{
                                    overflowY: 'auto',
                                    maxHeight: 'calc(85vh - 140px)',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                        },
                                    },
                                }}
                            >
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Leave Type</InputLabel>
                                    <Select
                                        name="leave_type_id"
                                        value={leaveForm.leave_type_id}
                                        onChange={(e) => handleInputChange(e as any)}
                                        required
                                        label="Leave Type"
                                    >
                                        {leaveBalance.map((balance: LeaveBalance) => (
                                            <MenuItem key={balance.leave_type_id} value={balance.leave_type_id}>
                                                {balance.leave_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Title"
                                    name="title"
                                    value={leaveForm.title}
                                    onChange={handleInputChange}
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    name="start_date"
                                    type="date"
                                    value={leaveForm.start_date}
                                    onChange={handleInputChange}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: getFinancialYearStartDate(), // Earliest = start of current financial year
                                    }}
                                    helperText="You can apply for past dates within the current financial year (e.g. for offline/email-approved leaves)"
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    label="End Date"
                                    name="end_date"
                                    type="date"
                                    value={leaveForm.end_date}
                                    onChange={handleInputChange}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: leaveForm.start_date || getFinancialYearStartDate(), // Cannot be before start date
                                    }}
                                    helperText="End date must be same as or after start date"
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Reason"
                                    name="body"
                                    value={leaveForm.body}
                                    onChange={handleInputChange}
                                    required
                                    multiline
                                    rows={4}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setShowLeaveModal(false)}>Cancel</Button>
                                <Button type="submit" variant="contained" disabled={loading}>
                                    {loading ? <CircularProgress size={20} /> : 'Submit'}
                                </Button>
                            </DialogActions>
                        </form>
                    </Dialog>

                    {/* Edit Leave Modal */}
                    <Dialog
                        open={showEditModal}
                        onClose={() => setShowEditModal(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Edit Leave Request</DialogTitle>
                        <form onSubmit={handleEditLeave}>
                            <DialogContent>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Leave Type</InputLabel>
                                    <Select
                                        name="leave_type_id"
                                        value={leaveForm.leave_type_id}
                                        onChange={(e) => handleInputChange(e as any)}
                                        required
                                        label="Leave Type"
                                    >
                                        {leaveBalance.map((balance: LeaveBalance) => (
                                            <MenuItem key={balance.leave_type_id} value={balance.leave_type_id}>
                                                {balance.leave_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label="Title"
                                    name="title"
                                    value={leaveForm.title}
                                    onChange={handleInputChange}
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    name="start_date"
                                    type="date"
                                    value={leaveForm.start_date}
                                    onChange={handleInputChange}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: getFinancialYearStartDate(), // Earliest = start of current financial year
                                    }}
                                    helperText="You can apply for past dates within the current financial year (e.g. for offline/email-approved leaves)"
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    label="End Date"
                                    name="end_date"
                                    type="date"
                                    value={leaveForm.end_date}
                                    onChange={handleInputChange}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: leaveForm.start_date || getFinancialYearStartDate(), // Cannot be before start date
                                    }}
                                    helperText="End date must be same as or after start date"
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Reason"
                                    name="body"
                                    value={leaveForm.body}
                                    onChange={handleInputChange}
                                    required
                                    multiline
                                    rows={4}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
                                <Button type="submit" variant="contained" disabled={loading}>
                                    {loading ? <CircularProgress size={20} /> : 'Update'}
                                </Button>
                            </DialogActions>
                        </form>
                    </Dialog>

                    {/* View Leave Details Modal */}
                    <Dialog
                        open={showViewModal}
                        onClose={() => setShowViewModal(false)}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                            sx: {
                                maxHeight: '85vh'
                            }
                        }}
                    >
                        <DialogTitle>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">Leave Request Details</Typography>
                                <Chip
                                    label={viewingLeave?.status.toUpperCase()}
                                    color={getStatusColor(viewingLeave?.status || '') as any}
                                    size="small"
                                />
                            </Box>
                        </DialogTitle>
                        <DialogContent
                            dividers
                            sx={{
                                overflowY: 'auto',
                                maxHeight: 'calc(85vh - 140px)',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                    },
                                },
                            }}
                        >
                            {viewingLeave && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    {/* Leave Type */}
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            LEAVE TYPE
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                label={viewingLeave.leave_type || 'Unknown'}
                                                sx={{
                                                    backgroundColor: getLeaveTypeColor(viewingLeave.leave_type || ''),
                                                    color: 'white',
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Title */}
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            TITLE
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {viewingLeave.title}
                                        </Typography>
                                    </Box>

                                    {/* Duration */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                START DATE
                                            </Typography>
                                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                                {formatDate(viewingLeave.start_date)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                END DATE
                                            </Typography>
                                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                                {formatDate(viewingLeave.end_date)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                DURATION
                                            </Typography>
                                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500, color: 'primary.main' }}>
                                                {calculateDays(viewingLeave.start_date, viewingLeave.end_date)} days
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Reason */}
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            REASON / DESCRIPTION
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                            {viewingLeave.body || 'No description provided'}
                                        </Typography>
                                    </Box>

                                    {/* Attachment */}
                                    {viewingLeave.attachment && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                ATTACHMENT
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <a href={viewingLeave.attachment} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                                    View Attachment
                                                </a>
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Request Info */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                APPLIED ON
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {formatDate(viewingLeave.created_at)}
                                            </Typography>
                                        </Box>
                                        {viewingLeave.approval_time && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                    {viewingLeave.status === 'approved' ? 'APPROVED ON' : 'DECLINED ON'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    {formatDate(viewingLeave.approval_time)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Managed By */}
                                    {viewingLeave.managed_by_name && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                {viewingLeave.status === 'approved' ? 'APPROVED BY' : 'DECLINED BY'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {viewingLeave.managed_by_name}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowViewModal(false)} variant="outlined">
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Withdraw Confirmation Dialog */}
                    <Dialog
                        open={withdrawConfirm.open}
                        onClose={() => !withdrawing && setWithdrawConfirm({ open: false, leaveId: null, title: '' })}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>Withdraw Leave</DialogTitle>
                        <DialogContent>
                            <Typography>
                                Are you sure you want to withdraw the leave request <strong>"{withdrawConfirm.title}"</strong>?
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Your leave balance will be restored after withdrawal.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setWithdrawConfirm({ open: false, leaveId: null, title: '' })}
                                disabled={withdrawing}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={handleWithdrawLeave}
                                disabled={withdrawing}
                            >
                                {withdrawing ? <CircularProgress size={20} /> : 'Withdraw'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Snackbar */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            </Container>
        </Layout>
    );
};

export default LeavePortal;
